if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.inventoryIcon = this.querySelector('.js-inventory-icon');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      this.inventoryContent = this.querySelector('.product-inventory__text');
      this.backorderedContent = this.querySelector('.product-inventory__backorder-content');
      this.variantDeliveryTime = this.querySelector('.variant-delivery-time');

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     * Updates the inventory notice icon based on inventory level
     * @param {string} inventoryLevel - the inventory level status
     */
    updateIcon(inventoryLevel) {
      if (!this.inventoryIcon) return;
      
      let iconHtml = '';
      
      if (inventoryLevel === 'very_low' || inventoryLevel === 'low') {
        // Use the out-of-stock icon for low and very low inventory
        iconHtml = this.renderIcon('icon-low-stock');
      } else if (inventoryLevel === 'backordered') {
        // Use backorder icon
        iconHtml = this.renderIcon('icon-backorder');
      } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
        // Use in-stock icon
        iconHtml = this.renderIcon('icon-in-stock');
      } else if (inventoryLevel === 'none') {
        // Use out-of-stock icon
        iconHtml = this.renderIcon('icon-out-of-stock');
      }
      
      this.inventoryIcon.innerHTML = iconHtml;
    }

    /**
     * Helper method to render an icon from a snippet
     * @param {string} iconName - the name of the icon snippet
     * @returns {string} HTML for the icon
     */
    renderIcon(iconName) {
      const iconSvg = {
        'icon-in-stock': '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z" fill="#16A34A"/><path d="M6 9C7.65685 9 9 7.65685 9 6C9 4.34315 7.65685 3 6 3C4.34315 3 3 4.34315 3 6C3 7.65685 4.34315 9 6 9Z" fill="white"/></svg>',
        'icon-low-stock': '<svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12.3333C9.31371 12.3333 12 9.64696 12 6.33325C12 3.01954 9.31371 0.333252 6 0.333252C2.68629 0.333252 0 3.01954 0 6.33325C0 9.64696 2.68629 12.3333 6 12.3333Z" fill="#2C7E3F"/><path d="M6 9.33325C7.65685 9.33325 9 7.99011 9 6.33325C9 4.6764 7.65685 3.33325 6 3.33325C4.34315 3.33325 3 4.6764 3 6.33325C3 7.99011 4.34315 9.33325 6 9.33325Z" fill="white"/></svg>',
        'icon-out-of-stock': '<svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12.3333C9.31371 12.3333 12 9.64696 12 6.33325C12 3.01954 9.31371 0.333252 6 0.333252C2.68629 0.333252 0 3.01954 0 6.33325C0 9.64696 2.68629 12.3333 6 12.3333Z" fill="#FFCA42"/><path d="M6 9.33325C7.65685 9.33325 9 7.99011 9 6.33325C9 4.6764 7.65685 3.33325 6 3.33325C4.34315 3.33325 3 4.6764 3 6.33325C3 7.99011 4.34315 9.33325 6 9.33325Z" fill="white"/></svg>',
        'icon-backorder': '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z" fill="#166533"/><path d="M6 9C7.65685 9 9 7.65685 9 6C9 4.34315 7.65685 3 6 3C4.34315 3 3 4.34315 3 6C3 7.65685 4.34315 9 6 9Z" fill="white"/></svg>'
      };
      
      return iconSvg[iconName] || '';
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(count, available, inventoryPolicy) {
      if (count === 0 && !available && this.hidden) {
        return;
      }

      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      // Always show in_stock status when it's normal or in_stock
      if (inventoryLevel === 'normal') {
        inventoryLevel = 'in_stock';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))
        || inventoryLevel === 'in_stock') {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);
        
        // Update the icon
        this.updateIcon(inventoryLevel);

        // show the default inventory layout first
        this.inventoryContent.style.display = "flex";
        this.backorderedContent.style.display = "none";
        this.variantDeliveryTime.style.display = "none";

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = theme.strings.backordered || 'Backordered';

          // show the backordered content instead of default inventory layout
          this.inventoryContent.style.display = "none";
          this.backorderedContent.style.display = "flex";
          this.variantDeliveryTime.style.display = "block";
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock || 'Very low stock';
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock || 'Low stock';
        } else if (inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock || 'In stock';
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock || 'Out of stock';

          // keep the default "Out of stock" text and additionally show the delivery time
          this.variantDeliveryTime.style.display = "block";
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
